/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

import { css, jsx } from '@compiled/react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';

import { closeReadingAidsPopup } from '../../pm-plugins/ai-definitions/commands';
import type { AIDefinitionsPlugin, ReadingAidsPopup } from '../../types';
import { READING_AIDS_POPUP_PORTAL_CONTAINER } from '../../utils/const';

const readingAidsPopupPortalContainerStyle = css({
	position: 'relative',
});

export type Props = {
	// api is mandatory but can be undefined
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	editorView: EditorView;
	contentId?: string;
	ReadingAidsPopup?: ReadingAidsPopup;
};

/**
 * readingAidsPopupKey
 * - When there are multiple auto-highlights in the document,
 * - and the user clicks on one auto-highlight to trigger the ReadingAidsPopup, then click another.
 * - readingAidsPopupKey will prevent the first ReadingAidsPopup from switching to the loading state
 * - and let the second ReadingAidsPopup show loading state.
 *
 * - Right now we're hiding the first ReadingAidsPopup when we click the second one.
 * - But readingAidsPopupKey will save us an experiment check if we want to show the second ReadingAidsPopup in the future.
 */
export const DefineModal = (props: Props) => {
	const { api, contentId, editorView, ReadingAidsPopup } = props;
	const { aiDefinitionsState } = useSharedPluginState(api, ['aiDefinitions']);

	return (
		<>
			<div
				id={READING_AIDS_POPUP_PORTAL_CONTAINER}
				css={readingAidsPopupPortalContainerStyle}
			></div>
			{/* TypeScript isn't happy if extract these condition as a variable,  */}
			{contentId &&
			ReadingAidsPopup &&
			aiDefinitionsState &&
			aiDefinitionsState.isReadingAidsPopupOpen &&
			aiDefinitionsState.readingAidsPopupKey &&
			aiDefinitionsState.readingAidsPopupProps ? (
				<ReadingAidsPopup
					key={aiDefinitionsState.readingAidsPopupKey}
					{...aiDefinitionsState.readingAidsPopupProps}
					isReadingAidsOpen
					popupPortalContainerId={READING_AIDS_POPUP_PORTAL_CONTAINER}
					contentId={contentId}
					onClose={() => {
						closeReadingAidsPopup()(editorView.state, editorView.dispatch);
					}}
				/>
			) : null}
		</>
	);
};
